// External
import { Formio } from '@formio/js'
import { v4 as randomUUID } from 'uuid'
import NovtiFormioComponents from '@novtilab/novti-formio-components'

// Local
import store from '@/store'
import i18n from '@/libs/i18n'
import { loadFormioBootstrap } from '@core/utils/utils'

export {
  showNoFormNotice,
  isMissingFormId,
  resolveCanvasFormId,
  resolveInitialTraitFormId,
} from './form-empty-state'

// Use the components
Formio.use(NovtiFormioComponents)

/**
 * Updates the form of the current page
 *
 * @param {string} formId The form ID
 * @returns {Promise} Containing the response data
 */
export function updatePageForm(formId) {
  return store.dispatch('pages/updateCurrent', {
    formId,
  })
}

/**
 * Populates the form options
 *
 * @param {*} model
 */
export function populateFormOptions(model) {
  store.dispatch('forms/fetchAll')
    .then(() => {
      const formTrait = model.get('traits').where({ name: 'form' })[0]

      if (!formTrait) {
        return
      }

      const forms = store.getters['forms/getAll']

      formTrait.set('options', [
        { value: '', name: i18n.t('Select a form') },
        ...forms
          .map(opt => ({ value: opt.id, name: opt.description }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ])
    })
    .catch(error => {
      console.debug(error)

      store.dispatch('notify', {
        body: i18n.t('Something went wrong retrieving the available forms'),
        variant: 'danger',
      })
    })
}

/**
 * Ensures Formio Bootstrap templates match the live page / Form Preview,
 * independent of whether Form Builder was visited earlier in this SPA session.
 *
 * @returns {Promise<void>}
 */
const ensureFormioBootstrapTemplates = async () => {
  const formEngineIntegration = store.getters['forms/getFormEngineIntegration']
  const version = formEngineIntegration?.bootstrapVersion
  const bootstrap = await loadFormioBootstrap(version)

  // Same order as the live renderer: Bootstrap templates first, then Novti
  // components, so custom fields keep their templates after Formio.use().
  Formio.use(bootstrap)
  Formio.use(NovtiFormioComponents)
}

/**
 * Renders the form preview
 *
 * @param {*} editor
 * @param {*} model
 * @param {*} schema
 * @param {*} newValue
 */
export async function renderPreview(editor, model, schema, newValue = null) {
  const previewElement = editor.Canvas.getDocument().getElementById('formio')
  const currentPage = store.getters['pages/getCurrent']
  const formComponent = editor.DomComponents.componentsById.formio
  const nextFormId = newValue ?? currentPage?.data?.formId

  if (!previewElement) {
    return
  }

  // Sync the canvas attribute from the page form on load, or from the picker on change.
  if (formComponent && formComponent.getAttributes()?.form !== nextFormId) {
    formComponent.setAttributes({ form: nextFormId })
  }

  if (previewElement.formio && typeof previewElement.formio.destroy === 'function') {
    previewElement.formio.destroy(true)
  }

  previewElement.innerHTML = ''

  await ensureFormioBootstrapTemplates()

  Formio.createForm(previewElement, schema, {
    noAlerts: true,
    buttonSettings: {
      showCancel: false,
      showPrevious: false,
      showNext: false,
      showSubmit: false,
    },
  })

  // All child elements should be non-editable
  const updateAll = model => {
    model.set({ editable: false })
    model.get('components').each(model => updateAll(model))
  }

  updateAll(model)
}

/**
 * Migrate the old JSON structure to the new one
 *
 * @param {Object} json The old JSON structure
 * @param {Object} styles The old styles structure
 *
 * @returns {Object} The new JSON structure
 */
export const migrateJsonStructure = (json, styles) => {
  const data = {
    assets: [],
    styles: [],
    pages: [
      {
        id: randomUUID(),
        frames: [
          {
            id: randomUUID(),
            component: {
              type: 'wrapper',
              stylable: [
                'background',
                'background-color',
                'background-image',
                'background-repeat',
                'background-attachment',
                'background-position',
                'background-size',
              ],
              components: [],
              head: { type: 'head' },
              docEl: { tagName: 'html' },
            },
          },
        ],
      },
    ],
    symbols: [],
    dataSources: [],
  }

  // Helper function to recursively transform components
  const transformComponent = component => {
    // Convert classes to simple arrays
    const classes = component.classes?.map(c => (typeof c === 'object' ? c.name : c)) || []

    // Normalize the component structure
    const newComponent = {
      ...component,
      classes,
      components: component.components ? component.components.map(transformComponent) : [],
    }

    return newComponent
  }

  // Transform each top-level component in the old JSON into the new format
  data.pages[0].frames[0].component.components = json.map(transformComponent)
  data.styles = styles

  return data
}
